import { api } from '../api.ts';
import { uploadDocument, type OpenedDocument } from '../documents.ts';
import { fromBase64 } from '../crypto/primitives.ts';
import { ablegbareSafes, alleSafes, gaesteVon, safeVon, type SafeAnzeige } from '../safes.ts';
import { safeKacheln, tuerschild, untertitel } from './safes-ui.ts';
import { dokumentInKreisVerschieben, dokumentInOrganisationVerschieben, fachFuerOrdner, ladeKreiseGecacht, type GeoeffneterKreis } from '../schluesselkreise.ts';
import { safeSchluessel } from '../safes.ts';
import {
  ablegeFeld,
  clear,
  el,
  formatBytes,
  formatDate,
  hinweis,
  iconKnopf,
  infoSymbol,
  karte,
  waehleAus,
  type AblegeFeld,
} from '../ui.ts';
import { frageSucheKarte } from './start.ts';
import { dienste, freigabeAufheben, freigabeFuer, geladeneDienste, nachAblage, ordnerFreigeben } from '../dienste.ts';
import {
  ablagezielSetzen,
  aktuellesAblageziel,
  aufbereiten,
  buddyKundeSetzen,
  buddyKunden,
  buddyHinweis,
  datenVerwerfen,
  fehlertext,
  kopfzeile,
  metadatenAuffrischen,
  oeffnen,
  herunterladen,
  ordnerDesSafes,
  ordnerMitUnterordnern,
  root,
  safeIdVon,
  sichtbareDokumente,
  sichtbareOrdner,
  sorgeFuerEntschluesselte,
  suchbereichSetzen,
  stapelMeldung,
  stapelVerarbeiten,
  state,
  zeigeSchritt,
  type Ordner,
  type StapelErgebnis,
} from './gemeinsam.ts';

/**
 * Ansicht „Dokumente": oben das Ablegen, darunter Ordnerbaum und Liste.
 *
 * Das ist die einzige Ansicht, die alle Dokumente entschlüsselt braucht –
 * die Dateinamen stehen nur verschlüsselt auf dem Server.
 */

export async function renderDokumente(): Promise<void> {
  clear(root);
  root.append(kopfzeile('dokumente'), el('p', { class: 'loading' }, 'Dokumente werden geladen …'));

  try {
    await sorgeFuerEntschluesselte();
  } catch (error) {
    clear(root);
    root.append(kopfzeile('dokumente'), hinweis(fehlertext(error), 'warnung'));
    return;
  }

  // Beim Buddy steht die Wahl des Kunden ganz oben: Alles darunter — Liste,
  // Ordner, Suche und KI-Frage — zeigt ausschließlich diese eine Freigabe.
  const istBuddy = state.me!.role === 'buddy';
  if (istBuddy) sorgeFuerKundenwahl();
  else {
    await dienste();
    kreiseDerAnsicht = state.me!.personId ? await ladeKreiseGecacht().catch(() => []) : [];
  }

  const abschnitte = [
    kopfzeile('dokumente'),
    istBuddy ? buddyHinweis() : null,
    istBuddy ? kundenwahlKarte() : null,
    istBuddy ? frageSucheKarte() : null,
    istBuddy ? null : safeWahlKarte(),
    // Ein Gast ohne irgendeinen Safe zum Ablegen sieht keine Ablagekarte.
    !istBuddy && state.me!.gast && ablegbareSafes().length === 0 ? null : uploadKarte(),
    dokumenteKarte(),
    istBuddy ? null : dienstKarte(),
  ].filter((abschnitt): abschnitt is HTMLElement => abschnitt !== null);

  clear(root);
  root.append(...abschnitte);
}

// --- Wessen Unterlagen? (nur Buddy) -----------------------------------------

/**
 * Sorgt dafür, dass immer genau ein Kunde gewählt ist.
 *
 * Ohne Vorauswahl stünde der Buddy vor einer leeren Ansicht und müsste erst
 * etwas anklicken; steht dagegen eine Wahl aus einer Freigabe, die inzwischen
 * abgelaufen oder widerrufen ist, zeigte die Ansicht dauerhaft nichts an.
 * Beides fängt diese Funktion ab.
 */
function sorgeFuerKundenwahl(): void {
  const kunden = buddyKunden();
  if (kunden.length === 0) {
    buddyKundeSetzen(null);
    return;
  }
  if (!kunden.some((kunde) => kunde.id === state.buddyKunde)) {
    buddyKundeSetzen(kunden[0]!.id);
  }
}

function kundenwahlKarte(): HTMLElement {
  const kunden = buddyKunden();

  if (kunden.length === 0) {
    return karte(
      el('h2', {}, 'Freigegebene Unterlagen'),
      el(
        'p',
        { class: 'leise' },
        'Derzeit hat Ihnen niemand Zugriff erteilt. Sobald eine Kundin oder ein ' +
          'Kunde eine Freigabe erteilt, erscheinen die Unterlagen hier.',
      ),
    );
  }

  // Bei nur einer Freigabe wäre eine Auswahlliste ein Bedienschritt ohne
  // Entscheidung — dann genügt der Name.
  if (kunden.length === 1) {
    const [einziger] = kunden;
    return karte(
      el('h2', {}, `Unterlagen von ${einziger!.name}`),
      el(
        'p',
        { class: 'leise' },
        `${anzahlFuer(einziger!.id)} freigegebene(s) Dokument(e). Weitere Freigaben ` +
          'liegen derzeit nicht vor.',
      ),
    );
  }

  const auswahl = el(
    'select',
    {
      id: 'buddy-kunde',
      onchange: (event: Event) => {
        buddyKundeSetzen((event.target as HTMLSelectElement).value);
        void renderDokumente();
      },
    },
    ...kunden.map((kunde) =>
      el(
        'option',
        { value: kunde.id, ...(kunde.id === state.buddyKunde ? { selected: 'selected' } : {}) },
        `${kunde.name} (${anzahlFuer(kunde.id)})`,
      ),
    ),
  );

  return karte(
    el('h2', {}, 'Wessen Unterlagen?'),
    el(
      'p',
      {},
      `${kunden.length} Kundinnen und Kunden haben Ihnen gerade Zugriff erteilt. ` +
        'Alles Weitere auf dieser Seite — Liste, Ordner, Suche und Frage — betrifft ' +
        'ausschließlich die hier gewählte Person.',
    ),
    el('label', { for: 'buddy-kunde' }, 'Freigabe auswählen'),
    auswahl,
  );
}

function anzahlFuer(customerId: string): number {
  return state.opened.filter((dokument) => dokument.meta.customer_id === customerId).length;
}

// --- Ablegen ---------------------------------------------------------------

/** Das Dateifeld der aktuellen Ansicht – von `ablegen()` gebraucht. */
let feldDerAnsicht: AblegeFeld | null = null;

/**
 * Was nach einem Lauf nicht abgelegt wurde.
 *
 * Nach dem Ablegen wird die ganze Ansicht neu aufgebaut, samt frischem
 * Dateifeld. Ohne diesen Zwischenspeicher wären die fehlgeschlagenen
 * Dateien aus der Auswahl verschwunden und müssten von Hand neu
 * herausgesucht werden.
 */
let restNachLauf: File[] = [];

function uploadKarte(): HTMLElement {
  const status = el('div', { id: 'upload-status' });

  const feld = ablegeFeld({
    id: 'datei',
    // `image/*` steht mit dabei, damit das Telefon die Kamera als Quelle
    // anbietet. Bewusst OHNE das Attribut `capture`: Das würde direkt die
    // Kamera öffnen und die Auswahl aus den vorhandenen Fotos verhindern.
    accept: '.pdf,.txt,.md,.csv,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.bmp,.tif,.tiff,.avif,image/*',
    beschriftung: 'Dateien auswählen (PDF, Fotos oder Textdateien)',
    aufforderung: 'Dateien hierher ziehen',
    erklaerung:
      'Ein Foto Ihrer Unterlagen genügt: Es wird passend zugeschnitten, gerade ' +
      'gerückt und als PDF abgelegt. Eingescannte PDF werden dabei lesbar ' +
      'gemacht, damit Sie später darin suchen können. Mehrere Dateien werden ' +
      'nacheinander abgearbeitet – die Einstellungen unten gelten für alle.',
  });
  feldDerAnsicht = feld;
  if (restNachLauf.length > 0) {
    feld.uebernimm(restNachLauf);
    restNachLauf = [];
  }

  const kaestchen = (
    id: string,
    beschriftung: string,
    erklaerung: string,
  ): HTMLElement =>
    el(
      'div',
      { style: 'display:flex;gap:.6rem;align-items:center;margin:.9rem 0' },
      el('input', { type: 'checkbox', id, checked: true, style: 'width:auto;margin:0' }),
      el('label', { for: id, style: 'margin:0;font-weight:600' }, beschriftung),
      infoSymbol(erklaerung),
    );

  // Der Knopf sagt, wie viel gleich passiert: Bei zwanzig ausgewählten
  // Dateien ist „Sicher ablegen" zu wenig Information vor einem Lauf, der
  // mehrere Minuten dauert.
  const knopf = el('button', { id: 'ablegen', onclick: () => void ablegen(status) }, 'Sicher ablegen');
  feld.beiAenderung((dateien) => {
    knopf.textContent =
      dateien.length > 1 ? `${dateien.length} Dokumente sicher ablegen` : 'Sicher ablegen';
  });

  // Familien-Safes: „Wohin legen?" – nur, wenn es mehr als einen Safe gibt, in den man
  // ablegen darf. Die Ordnerliste darunter folgt der Wahl.
  const ziele = state.me?.role === 'customer' ? ablegbareSafes() : [];
  const ordnerPlatz = el('div', {});
  const zeichneOrdner = (safeId: string | null): void => {
    clear(ordnerPlatz);
    ordnerPlatz.append(ordnerAuswahl(safeId));
  };
  zeichneOrdner(aktuellesAblageziel());
  const zielKacheln =
    ziele.length > 1
      ? safeKacheln({
          frage: 'Wohin legen?',
          safes: ziele,
          gewaehlt: aktuellesAblageziel(),
          name: 'ablageziel',
          onWahl: (kundeId) => {
            ablagezielSetzen(kundeId);
            zeichneOrdner(kundeId);
          },
        })
      : null;

  return karte(
    el('h2', {}, 'Dokumente ablegen'),
    zielKacheln,
    feld.element,

    kaestchen(
      'ki-opt-in',
      'Automatisch einsortieren',
      'Der Text dieses einen Dokuments wird einmalig an unseren KI-Dienst ' +
        'gesendet, um Kategorie, Betrag, Datum und Absender zu erkennen. Das ' +
        'Dokument wandert dann von selbst in den passenden Ordner. Der Text ' +
        'wird dort nicht gespeichert. Ohne Häkchen landet das Dokument unter ' +
        '„Nicht sortiert" – auffindbar bleibt es trotzdem.',
    ),

    kaestchen(
      'umbenennen',
      'Dateiname vereinheitlichen',
      'Die Datei bekommt einen Namen nach festem Muster: Datum, Art, ' +
        'Absender, Betrag – zum Beispiel „2026-06-15_Rechnung_Stadtwerke_' +
        '249-90EUR.pdf". So sind Ihre Unterlagen auf einen Blick zu ' +
        'unterscheiden und sortieren sich von selbst nach Datum. Setzt ' +
        '„Automatisch einsortieren" voraus; erkennt die KI nichts, bleibt der ' +
        'ursprüngliche Name erhalten.',
    ),

    ordnerPlatz,
    status,
    el('div', { class: 'knopfreihe' }, knopf),
  );
}

/** Zielordner von Hand wählen – überstimmt den Vorschlag der KI. */
function ordnerAuswahl(safeId: string | null): HTMLElement {
  const auswahl = el('select', { id: 'zielordner' }) as HTMLSelectElement;
  auswahl.append(el('option', { value: '' }, 'Automatisch bestimmen'));
  for (const ordner of ordnerNachPfad(safeId)) {
    auswahl.append(el('option', { value: ordner.id }, ordner.pfad));
  }
  return el(
    'div',
    {},
    el('label', { for: 'zielordner' }, 'Ordner'),
    auswahl,
  );
}

async function ablegen(status: HTMLElement): Promise<void> {
  const feld = feldDerAnsicht;
  const useAi = root.querySelector<HTMLInputElement>('#ki-opt-in')!.checked;
  const renameFile = root.querySelector<HTMLInputElement>('#umbenennen')!.checked;
  const zielordner = root.querySelector<HTMLSelectElement>('#zielordner')!.value || null;
  const button = root.querySelector<HTMLButtonElement>('#ablegen')!;
  const dateien = feld?.dateien() ?? [];

  clear(status);
  if (dateien.length === 0) {
    status.append(hinweis('Bitte wählen Sie zuerst eine Datei aus.', 'warnung'));
    return;
  }

  button.disabled = true;
  feld?.sperren(true);
  try {
    // In welchen Safe – und für welche Gäste des Safes mitverpacken (Familien-Safes).
    const safeOption = await safeFuerAblage(aktuellesAblageziel());
    const bericht = await stapelVerarbeiten(dateien, status, async (datei, zeile) => {
      zeigeSchritt(zeile, 'Datei wird gelesen');
      const bytes = new Uint8Array(await datei.arrayBuffer());

      const aufbereitet = await aufbereiten(datei, bytes, zeile);
      if (!aufbereitet) return { art: 'uebersprungen', text: 'Es wurde nichts abgelegt.' };

      await uploadDocument({
        aufbereitet,
        useAi,
        renameFile,
        folderId: zielordner,
        safe: safeOption,
        onProgress: (schritt) => zeigeSchritt(zeile, schritt),
      });

      const hinweise = [...aufbereitet.hinweise];
      if (aufbereitet.veraendert && aufbereitet.dateiname !== datei.name) {
        hinweise.push(
          `Aus dem Bild „${datei.name}" wurde ein PDF – zugeschnitten, gerade ` +
            'gerückt und mit lesbarem Text hinterlegt.',
        );
      }
      return {
        art: 'erfolg',
        text: `„${aufbereitet.dateiname}" wurde verschlüsselt abgelegt.`,
        hinweise,
      } satisfies StapelErgebnis;
    });

    if (bericht.erfolge === 0) {
      status.prepend(stapelMeldung(bericht));
      return;
    }

    // Die Ansicht muss neu aufgebaut werden (neue Dokumente, neue Ordner-
    // stände). Der Verlauf des Laufs wandert dabei mit in das frische
    // Statusfeld – sonst wäre nach dem Neuaufbau nicht mehr zu sehen, was
    // mit welcher Datei passiert ist.
    const verlauf = [...status.childNodes];
    const erledigt = new Set(bericht.erledigt);
    restNachLauf = dateien.filter((datei) => !erledigt.has(datei));
    datenVerwerfen();
    await renderDokumente();
    root.querySelector<HTMLElement>('#upload-status')?.append(stapelMeldung(bericht), ...verlauf);
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    button.disabled = false;
    feld?.sperren(false);
  }
}

/** Die aufgeschlossenen Kreise (Fächer) aller Safes – für Marken, Ablegen und Verschieben. */
let kreiseDerAnsicht: GeoeffneterKreis[] = [];

const elternVon = (id: string): string | null => state.ordner.find((o) => o.id === id)?.parent_id ?? null;

/** Das Fach, in dem ein Ordner liegt (falls aufgeschlossen). */
function fachVon(folderId: string | null): GeoeffneterKreis | null {
  return fachFuerOrdner(folderId, kreiseDerAnsicht, elternVon);
}

/**
 * Was `uploadDocument` über den Ziel-Safe wissen muss: seinen öffentlichen Schlüssel
 * und die Gäste, für die mitverpackt wird (Fächer schon um Unterordner erweitert).
 * Ohne Safe (Altkonto ohne Organisation) läuft das Ablegen wie früher.
 */
async function safeFuerAblage(safeId: string | null): Promise<Parameters<typeof uploadDocument>[0]['safe']> {
  const safe = safeVon(safeId);
  if (!safe?.schluessel) return undefined;
  const gaeste = await gaesteVon(safe.kundeId).catch(() => []);
  return {
    kundeId: safe.kundeId,
    publicKeyRaw: fromBase64(safe.schluessel.publicKey),
    algorithm: safe.schluessel.algorithm,
    fachFuer: (folderId) => {
      const fach = fachVon(folderId);
      return fach ? { id: fach.id, publicKeyRaw: fach.publicKeyRaw, algorithm: fach.algorithm } : null;
    },
    gaeste: gaeste
      .filter((g) => g.publicKey && g.algorithm)
      .map((g) => ({
        personId: g.personId,
        publicKeyRaw: fromBase64(g.publicKey!),
        algorithm: g.algorithm!,
        umfang: g.umfang,
        faecher: [...ordnerMitUnterordnern(g.faecher)],
      })),
  };
}

/** Kacheln „Welchen Safe anzeigen?" – derselbe Suchbereich wie auf der Startseite. */
function safeWahlKarte(): HTMLElement | null {
  const safes = alleSafes();
  if (safes.length < 2) return null;
  return karte(
    safeKacheln({
      frage: 'Welchen Safe möchten Sie sehen?',
      safes,
      gewaehlt: state.suchbereich,
      mitUeberall: true,
      name: 'anzeige-safe',
      zeile: (safe) => {
        if (safe.gesperrt) return null;
        const n = state.opened.filter((d) => safeIdVon(d.meta.safe_kunde_id) === safe.kundeId).length;
        return `${n} Dokument${n === 1 ? '' : 'e'}`;
      },
      onWahl: (kundeId) => {
        suchbereichSetzen(kundeId);
        void renderDokumente();
      },
    }),
  );
}

// --- Dokumentenliste -------------------------------------------------------

/** Ordner als flache Liste mit vollem Pfad – für Auswahlfelder; auf einen Safe begrenzt, wenn es Safes gibt. */
function ordnerNachPfad(safeId?: string | null): Array<{ id: string; pfad: string }> {
  const pfad = (ordner: Ordner): string => {
    const eltern = state.ordner.find((o) => o.id === ordner.parent_id);
    return eltern ? `${pfad(eltern)} / ${ordner.name}` : ordner.name;
  };
  const quelle = safeId !== undefined && alleSafes().length > 0 ? ordnerDesSafes(safeId) : state.ordner;
  return quelle
    .map((o) => ({ id: o.id, pfad: pfad(o) }))
    .sort((a, b) => a.pfad.localeCompare(b.pfad, 'de'));
}

function dokumenteKarte(): HTMLElement {
  const sichtbar = sichtbareDokumente();
  const nichtLesbar = state.documents.length - state.opened.length;
  const ohneOrdner = sichtbar.filter((d) => !d.meta.folder_id);
  const istBuddy = state.me!.role === 'buddy';

  // Familien-Safes: ein Schrank je Safe, darin der gewohnte Ordnerbaum.
  const safes = istBuddy ? [] : alleSafes().filter((s) => !state.suchbereich || s.kundeId === state.suchbereich);
  if (safes.length > 0) {
    return karte(
      el('h2', { style: 'margin:0' }, `Ihre Dokumente (${sichtbar.length})`),
      nichtLesbar > 0
        ? hinweis(
            `${nichtLesbar} Dokument(e) konnten nicht entschlüsselt werden. Bitte ` +
              'melden Sie sich bei Ihrem Ansprechpartner.',
            'warnung',
          )
        : null,
      ...safes.map((safe) => schrank(safe)),
    );
  }

  return karte(
    el(
      'div',
      { style: 'display:flex;flex-wrap:wrap;gap:1rem;align-items:baseline;justify-content:space-between' },
      el(
        'h2',
        { style: 'margin:0' },
        istBuddy
          ? `Freigegebene Dokumente (${sichtbar.length})`
          : `Ihre Dokumente (${state.opened.length})`,
      ),
      el(
        'button',
        { class: 'sekundaer', onclick: () => void neuerOrdner(null) },
        'Neuer Ordner',
      ),
    ),
    nichtLesbar > 0
      ? hinweis(
          `${nichtLesbar} Dokument(e) konnten nicht entschlüsselt werden. Bitte ` +
            'melden Sie sich bei Ihrem Ansprechpartner.',
          'warnung',
        )
      : null,
    sichtbar.length === 0 && sichtbareOrdner().length === 0
      ? el('p', { class: 'leise' }, 'Noch keine Dokumente abgelegt.')
      : el(
          'ul',
          { class: 'ordnerbaum' },
          ...ordnerZweig(null),
          // Alles ohne Ordner steht am Ende, nicht dazwischen – sonst sucht
          // man es zwischen den Ordnern.
          ohneOrdner.length > 0
            ? el(
                'li',
                {},
                el(
                  'div',
                  { class: 'ordnerzeile' },
                  el('span', {}, 'Nicht sortiert'),
                  el('span', { class: 'anzahl' }, `${ohneOrdner.length} Dokument(e)`),
                ),
                dokumentListe(ohneOrdner),
              )
            : null,
        ),
  );
}

/** Ein Safe als Schrank: Türschild, Name, Ordnerbaum, Unsortiertes. */
function schrank(safe: SafeAnzeige): HTMLElement {
  const dokumente = state.opened.filter((d) => safeIdVon(d.meta.safe_kunde_id) === safe.kundeId);
  const ordner = state.ordner.filter((o) => safeIdVon(o.safe_kunde_id) === safe.kundeId);
  const ohneOrdner = dokumente.filter((d) => !d.meta.folder_id);
  const offen = offeneSchraenke.has(safe.kundeId) || alleSafes().length === 1 || !!state.suchbereich;

  const details = el(
    'details',
    { class: 'schrank', style: `--k:${safe.farbe}`, ...(offen ? { open: true } : {}) },
    el(
      'summary',
      { class: 'schrank-kopf' },
      tuerschild(safe),
      el('span', { class: 'schrank-titel' }, el('b', {}, safe.titel), el('small', {}, `${untertitel(safe)} · ${dokumente.length} Dokument${dokumente.length === 1 ? '' : 'e'}`)),
      safe.darfAblegen && !safe.gesperrt
        ? el('button', { class: 'sekundaer klein', type: 'button', onclick: (e: Event) => { e.preventDefault(); void neuerOrdner(null, safe.kundeId); } }, 'Neuer Ordner')
        : null,
    ),
    safe.gesperrt
      ? hinweis('Der Schlüssel zu diesem Safe kommt automatisch, sobald sich jemand aus der Familie, der ihn schon hat, das nächste Mal anmeldet.')
      : dokumente.length === 0 && ordner.length === 0
        ? el('p', { class: 'leise' }, 'Hier liegt noch nichts.')
        : el(
            'ul',
            { class: 'ordnerbaum' },
            ...ordnerZweig(null, safe.kundeId),
            ohneOrdner.length > 0
              ? el(
                  'li',
                  {},
                  el('div', { class: 'ordnerzeile' }, el('span', {}, 'Nicht sortiert'), el('span', { class: 'anzahl' }, `${ohneOrdner.length} Dokument(e)`)),
                  dokumentListe(ohneOrdner),
                )
              : null,
          ),
  );
  details.addEventListener('toggle', () => {
    if (details.open) offeneSchraenke.add(safe.kundeId);
    else offeneSchraenke.delete(safe.kundeId);
  });
  return details;
}

/** Welche Schränke aufgeklappt sind – überlebt das Neuzeichnen wie `offeneOrdner`. */
const offeneSchraenke = new Set<string>();

/**
 * Welche Ordner aufgeklappt sind. Je Ebene höchstens einer: Öffnet man einen,
 * schließen sich seine Geschwister, Unterordner lassen sich darin weiter
 * öffnen. Lebt außerhalb des Baums, weil jede Aktion (Verschieben,
 * Umbenennen …) die Ansicht neu aufbaut — sonst klappte danach alles zu.
 */
const offeneOrdner = new Set<string>();

/** Dokumente eines Ordners einschließlich aller Unterordner. */
function anzahlMitUnterordnern(ordnerId: string): number {
  const direkt = sichtbareDokumente().filter((d) => d.meta.folder_id === ordnerId).length;
  return sichtbareOrdner()
    .filter((o) => o.parent_id === ordnerId)
    .reduce((summe, kind) => summe + anzahlMitUnterordnern(kind.id), direkt);
}

/** Ein Ast des Ordnerbaums, rekursiv. */
function ordnerZweig(parentId: string | null, safeId?: string): HTMLElement[] {
  const geschwister = sichtbareOrdner()
    .filter((o) => o.parent_id === parentId && (parentId !== null || safeId === undefined || safeIdVon(o.safe_kunde_id) === safeId))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const geschwisterIds = geschwister.map((o) => o.id);

  return geschwister.map((ordner) => {
    const eigene = sichtbareDokumente().filter((d) => d.meta.folder_id === ordner.id);
    const kinder = ordnerZweig(ordner.id);
    const gesamt = anzahlMitUnterordnern(ordner.id);

    const inhalt = kinder.length > 0 || eigene.length > 0;

    const details = el(
      'details',
      offeneOrdner.has(ordner.id) && inhalt ? { open: true } : {},
      el(
        'summary',
        { class: 'ordnerzeile' },
        el('span', {}, `📁 ${ordner.name}`, kreiseDerAnsicht.some((k) => k.folderId === ordner.id) ? el('span', { class: 'fach-marke', title: 'Nur für bestimmte Leute' }, 'Fach') : null),
        el('span', { class: 'anzahl' }, `${gesamt} Dokument(e)`),
        ...dienstKnoepfe(ordner),
        iconKnopf('unterordner', 'Unterordner anlegen', () => void neuerOrdner(ordner.id, safeIdVon(ordner.safe_kunde_id))),
        iconKnopf('umbenennen', 'Ordner umbenennen', () => void ordnerUmbenennen(ordner)),
        iconKnopf('loeschen', 'Ordner löschen', () => void ordnerLoeschen(ordner), true),
      ),
      inhalt
        ? el('ul', {}, ...kinder, ...(eigene.length ? [el('li', {}, dokumentListe(eigene))] : []))
        : null,
    );

    details.addEventListener('toggle', () => {
      if (!details.open) {
        offeneOrdner.delete(ordner.id);
        return;
      }
      offeneOrdner.add(ordner.id);
      // Nur die Geschwister derselben Ebene schließen; was darüber offen
      // ist, trägt diesen Ordner und bleibt offen.
      const liste = details.parentElement?.parentElement;
      for (const id of geschwisterIds) {
        if (id !== ordner.id) offeneOrdner.delete(id);
      }
      liste?.querySelectorAll<HTMLDetailsElement>(':scope > li > details[open]').forEach((d) => {
        if (d !== details) d.open = false;
      });
    });

    return el('li', {}, details);
  });
}

function dokumentListe(dokumente: OpenedDocument[]): HTMLElement {
  return el(
    'ul',
    { class: 'dokumentliste kompakt' },
    ...dokumente.map((document) =>
      el(
        'li',
        {},
        el(
          'div',
          { class: 'dok-haupt' },
          el(
            'span',
            { class: 'dokumentname', title: document.filename },
            document.filename,
            document.meta.category_label
              ? el('span', { class: 'marke' }, document.meta.category_label)
              : null,
            document.meta.uploaded_via !== 'customer'
              ? el(
                  'span',
                  { class: 'marke blindablage' },
                  document.meta.uploaded_via === 'buddy' ? 'von uns' : document.meta.uploaded_via === 'dienst' ? 'vom Vertragsverwalter' : 'WhatsApp',
                )
              : null,
          ),
          el(
            'span',
            { class: 'dok-meta' },
            `${formatDate(document.meta.created_at)} · ${formatBytes(document.meta.size_bytes)}`,
          ),
        ),
        el(
          'div',
          { class: 'dok-aktionen' },
          el('button', { onclick: () => void oeffnen(document.id) }, 'Öffnen'),
          el('button', { onclick: () => void herunterladen(document.id) }, 'Herunterladen'),
          // In fremden Safes (Gast) wird weder verschoben noch gelöscht.
          state.me!.role === 'customer' && (safeVon(document.meta.safe_kunde_id)?.darfLoeschen ?? true)
            ? el('button', { onclick: () => void verschieben(document) }, 'Verschieben')
            : null,
          state.me!.role === 'customer' && (safeVon(document.meta.safe_kunde_id)?.darfLoeschen ?? true)
            ? el('button', { class: 'warnung', onclick: () => void loeschen(document) }, 'Löschen')
            : null,
        ),
      ),
    ),
  );
}

/** Zielordner im Dialog wählen – ohne Neuverschlüsselung des Dokuments. */
async function verschieben(document: OpenedDocument): Promise<void> {
  const ziel = await waehleAus('Wohin verschieben?', `„${document.filename}"`, [
    { wert: null, text: 'Nicht sortiert', aktuell: !document.meta.folder_id },
    ...ordnerNachPfad(safeIdVon(document.meta.safe_kunde_id)).map((o) => ({
      wert: o.id,
      text: o.pfad,
      aktuell: o.id === document.meta.folder_id,
    })),
  ]);

  // undefined = abgebrochen; null = bewusst nach "Nicht sortiert".
  if (ziel === undefined) return;

  try {
    await api.patch(`/api/documents/${document.id}`, { folderId: ziel });
    // Fächer (gemeinsame Safes): Hinein → für den Kreis verpacken; heraus → wieder für den Safe.
    const safe = safeVon(document.meta.safe_kunde_id);
    const fachNeu = fachVon(ziel);
    const warImFach = document.meta.wrap_ziel_art === 'kreis';
    if (safe?.zugang === 'mitglied' && safe.typ !== 'privat') {
      if (fachNeu && (!warImFach || document.meta.wrap_ziel_id !== fachNeu.id)) {
        await dokumentInKreisVerschieben(document.id, document.dek, fachNeu);
      } else if (!fachNeu && warImFach) {
        const schluessel = safeSchluessel(safe.kundeId);
        if (schluessel) await dokumentInOrganisationVerschieben(document.id, document.dek, { publicKeyRaw: schluessel.publicKeyRaw, algorithm: schluessel.algorithm });
      }
    }
    // In einen für einen Dienst freigegebenen Ordner? Dann bekommt er den Umschlag.
    await nachAblage(document.id, ziel, document.dek, state.ordner);
    await metadatenAuffrischen();
    await renderDokumente();
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

/**
 * Freigabe für Dienste (Vertragsverwalter der KF Zentrale) am Ordner. Nur Kunden; ein
 * Unterordner zeigt die geerbte Freigabe nur an.
 */
/**
 * Freigaben für Dienste sind ausgeblendet (Karsten, 12.10.2026: „Verträge" ist freigegeben,
 * Kunden sollen davon nichts sehen). Neue Ablagen in freigegebenen Ordnern verpackt
 * `nachAblage` weiterhin still mit. Zum Ändern einer Freigabe hier auf true setzen.
 */
const DIENSTE_SICHTBAR = false;

function dienstKnoepfe(ordner: Ordner): HTMLElement[] {
  if (!DIENSTE_SICHTBAR || state.me?.role !== 'customer') return [];
  return geladeneDienste().flatMap((dienst) => {
    const stand = freigabeFuer(dienst, ordner.id, state.ordner);
    if (!stand) return [];
    const kurz = dienst.name.split(' (')[0]!;
    return [el('span', { class: 'anzahl', title: stand === 'geerbt' ? `Über einen Elternordner für den ${kurz} freigegeben` : `Für den ${kurz} freigegeben` }, `✓ ${kurz}`)];
  });
}

/**
 * Freigaben für Dienste (nur das Konto, dem der Dienst gehört – das prüft der Server).
 * Bewusst eine eigene Karte statt Knöpfen in der Ordnerzeile: Knöpfe in <summary>
 * reagieren auf dem iPad nicht zuverlässig, und window.confirm blockieren manche Browser.
 */
function dienstKarte(): HTMLElement | null {
  const alle = geladeneDienste();
  if (!DIENSTE_SICHTBAR || state.me?.role !== 'customer' || !alle.length) return null;
  const pfad = (o: Ordner): string => {
    const teile = [o.name];
    let p = state.ordner.find((x) => x.id === o.parent_id);
    while (p && teile.length < 6) { teile.unshift(p.name); p = state.ordner.find((x) => x.id === p!.parent_id); }
    return teile.join(' / ');
  };
  return karte(...alle.map((dienst) => {
    const kurz = dienst.name.split(' (')[0]!;
    const meldung = el('div', {});
    const zeigen = (text: string, art: 'info' | 'warnung' | 'erfolg') => { clear(meldung); meldung.append(hinweis(text, art)); };
    const freigegeben = state.ordner.filter((o) => dienst.ordner.includes(o.id));
    const liste = el('ul', { class: 'dienst-ordner' }, ...freigegeben.map((o) => {
      const zeile = el('li', {}, el('span', {}, `📁 ${pfad(o)}`));
      const zuruecknehmen = el('button', { type: 'button', class: 'klein' }, 'Freigabe zurücknehmen');
      zuruecknehmen.addEventListener('click', () => {
        clear(zeile);
        const ja = el('button', { type: 'button', class: 'klein gefahr' }, 'Ja, zurücknehmen');
        const nein = el('button', { type: 'button', class: 'klein' }, 'Abbrechen');
        ja.addEventListener('click', () => {
          ja.setAttribute('disabled', '');
          void freigabeAufheben(dienst, o.id).then(() => renderDokumente()).catch((f) => zeigen(fehlertext(f), 'warnung'));
        });
        nein.addEventListener('click', () => void renderDokumente());
        zeile.append(el('span', {}, `„${pfad(o)}" nicht mehr für den ${kurz} freigeben? Er kann die Dokumente darin dann nicht mehr lesen.`), ja, nein);
      });
      zeile.append(zuruecknehmen);
      return zeile;
    }));
    const wahl = el('select', { 'aria-label': 'Ordner' },
      el('option', { value: '' }, 'Ordner wählen …'),
      ...state.ordner.filter((o) => !freigabeFuer(dienst, o.id, state.ordner)).map((o) => el('option', { value: o.id }, pfad(o))));
    const knopf = el('button', { type: 'button' }, 'Freigeben');
    const bereich = el('div', { class: 'dienst-freigabe' }, wahl, knopf);
    knopf.addEventListener('click', () => {
      const ordner = state.ordner.find((o) => o.id === (wahl as HTMLSelectElement).value);
      if (!ordner) { zeigen('Bitte zuerst einen Ordner wählen.', 'warnung'); return; }
      clear(bereich);
      const ja = el('button', { type: 'button' }, 'Ja, freigeben');
      const nein = el('button', { type: 'button', class: 'klein' }, 'Abbrechen');
      bereich.append(el('p', {}, `„${pfad(ordner)}" für den ${kurz} freigeben? Er kann danach alle Dokumente in diesem Ordner und seinen Unterordnern lesen – auch, was du künftig hier ablegst. Andere Dokumente sieht er nicht; jeder Zugriff steht im Protokoll.`), ja, nein);
      nein.addEventListener('click', () => void renderDokumente());
      ja.addEventListener('click', () => {
        ja.setAttribute('disabled', '');
        ja.textContent = 'wird freigegeben …';
        void ordnerFreigeben(dienst, ordner.id, sichtbareDokumente(), state.ordner)
          .then(() => renderDokumente())
          .catch((f) => { zeigen(fehlertext(f), 'warnung'); ja.removeAttribute('disabled'); ja.textContent = 'Ja, freigeben'; });
      });
    });
    return el('div', { class: 'stapel' },
      el('h2', {}, kurz),
      el('p', { class: 'leise' }, dienst.beschreibung ?? ''),
      freigegeben.length ? liste : el('p', {}, 'Noch kein Ordner freigegeben.'),
      bereich,
      meldung,
    );
  }));
}

async function neuerOrdner(parentId: string | null, safeId?: string | null): Promise<void> {
  const eltern = state.ordner.find((o) => o.id === parentId);
  const safe = safeVon(safeId ?? eltern?.safe_kunde_id);
  const name = window.prompt(
    eltern ? `Neuer Unterordner in „${eltern.name}"` : safe ? `Name des neuen Ordners in „${safe.titel}"` : 'Name des neuen Ordners',
  );
  if (!name?.trim()) return;
  try {
    // Mit Safe-Kennung, sobald es einen Safe gibt – auch der eigene private Safe
    // bekommt so Ordner, die zu ihm gehören statt nur zum Konto.
    await api.post('/api/folders', { name: name.trim(), parentId, ...(safe?.schluessel ? { kundeId: safe.kundeId } : {}) });
    await metadatenAuffrischen();
    await renderDokumente();
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

async function ordnerUmbenennen(ordner: Ordner): Promise<void> {
  const name = window.prompt('Neuer Name für diesen Ordner', ordner.name);
  if (!name?.trim() || name.trim() === ordner.name) return;
  try {
    await api.patch(`/api/folders/${ordner.id}`, { name: name.trim() });
    await metadatenAuffrischen();
    await renderDokumente();
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

async function ordnerLoeschen(ordner: Ordner): Promise<void> {
  const enthalten = sichtbareDokumente().filter((d) => d.meta.folder_id === ordner.id).length;
  const frage = enthalten
    ? `„${ordner.name}" löschen? Die ${enthalten} enthaltenen Dokumente bleiben ` +
      'erhalten und stehen danach unter „Nicht sortiert".'
    : `„${ordner.name}" löschen?`;
  if (!window.confirm(frage)) return;
  try {
    await api.delete(`/api/folders/${ordner.id}`);
    await metadatenAuffrischen();
    await renderDokumente();
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

async function loeschen(document: OpenedDocument): Promise<void> {
  if (
    !window.confirm(
      `„${document.filename}" endgültig löschen? Das lässt sich nicht rückgängig machen.`,
    )
  ) {
    return;
  }
  await api.delete(`/api/documents/${document.id}`);
  // Suchindex und KI-Vorbereitung kennen das Dokument noch – neu aufbauen.
  state.index?.clear();
  state.index = null;
  state.prepared = null;
  state.opened = state.opened.filter((entry) => entry.id !== document.id);
  await metadatenAuffrischen();
  await renderDokumente();
}
